import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { handleAuthError } from '../utils/auth';
import AIAnalysisModal from '../components/AIAnalysisModal';

const API_BASE_URL = 'https://best3intennis.com/best3intennis-api/api';

const StatsPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [opponents, setOpponents] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('me');
  const [scope, setScope] = useState('overall'); // 'overall' | 'match'
  const [selectedMatchId, setSelectedMatchId] = useState('all');
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  const [statsData, setStatsData] = useState({
    matches_list: [],
    stats: {
      total_matches: 0,
      matches_won: 0,
      matches_lost: 0,
      win_rate: 0,
      games_won: 0,
      games_lost: 0,
      total_winners: 0,
      aces: 0,
      winners: 0,
      total_errors: 0,
      double_faults: 0,
      serve_faults: 0,
      unforced_errors: 0,
      forced_errors: 0,
      service_games_played: 0,
      service_games_won: 0,
      service_hold_rate: 0,
    },
    matches_breakdown: [],
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      const queryParams = new URLSearchParams({
        player_id: selectedPlayerId,
        match_id: scope === 'match' ? selectedMatchId : 'all',
      });

      const response = await fetch(`${API_BASE_URL}/get_player_stats.php?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.success) {
        setOpponents(data.opponents || []);
        const mList = data.matches_list || [];
        setStatsData({
          matches_list: mList,
          stats: data.stats || {},
          matches_breakdown: data.matches_breakdown || [],
        });
        if (scope === 'match' && mList.length > 0) {
          const exists = mList.some(m => String(m.match_id) === String(selectedMatchId));
          if (!exists) {
            setSelectedMatchId(String(mList[0].match_id));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching player stats:', err);
      if (err.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        setError('Failed to load player stats.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedPlayerId, scope, selectedMatchId]);

  const [savedAnalysisInfo, setSavedAnalysisInfo] = useState(null);

  const checkSavedAnalysis = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const params = new URLSearchParams({
        analysis_type: scope === 'match' ? 'match' : 'player_overall',
      });
      if (scope === 'match' && selectedMatchId && selectedMatchId !== 'all') {
        params.append('match_id', selectedMatchId);
      } else if (selectedPlayerId) {
        params.append('player_id', selectedPlayerId);
      }
      params.append('_t', Date.now().toString());
      const res = await fetch(`${API_BASE_URL}/get_ai_analyses.php?${params.toString()}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Pragma': 'no-cache',
          'Cache-Control': 'no-cache',
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.analyses && data.analyses.length > 0) {
          setSavedAnalysisInfo(data.analyses[0]);
        } else {
          setSavedAnalysisInfo(null);
        }
      }
    } catch (_e) {
      // Ignore
    }
  }, [scope, selectedMatchId, selectedPlayerId]);

  useEffect(() => {
    fetchStats();
    checkSavedAnalysis();
  }, [fetchStats, checkSavedAnalysis]);

  const handlePlayerChange = (e) => {
    setSelectedPlayerId(e.target.value);
  };

  const handleScopeChange = (newScope) => {
    setScope(newScope);
    if (newScope === 'match') {
      const exists = statsData.matches_list.some(m => String(m.match_id) === String(selectedMatchId));
      if (!exists && statsData.matches_list.length > 0) {
        setSelectedMatchId(String(statsData.matches_list[0].match_id));
      }
    }
  };

  const stats = statsData.stats;
  const totalEvents = (stats.total_winners || 0) + (stats.total_errors || 0);
  const winnersPct = totalEvents > 0 ? Math.round((stats.total_winners / totalEvents) * 100) : 50;
  const errorsPct = 100 - winnersPct;

  const selectedPlayerName = selectedPlayerId === 'me'
    ? 'Me'
    : (opponents.find(o => String(o.id) === String(selectedPlayerId))?.name || 'Selected Opponent');

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            📊 Player & Match Statistics
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyze winners, mistake patterns, and serve performance
          </p>
        </div>

        {/* Player and Scope Filters */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Player Dropdown */}
          <div className="flex-1 md:flex-none">
            <label htmlFor="player-select" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Player
            </label>
            <select
              id="player-select"
              value={selectedPlayerId}
              onChange={handlePlayerChange}
              className="w-full md:w-48 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="me">👤 Me</option>
              {opponents.map(opp => (
                <option key={opp.id} value={opp.id}>
                  🎾 {opp.name || `${opp.firstName || ''} ${opp.lastName || ''}`}
                </option>
              ))}
            </select>
          </div>

          {/* Scope Toggle */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Scope
            </label>
            <div className="flex rounded-lg border border-gray-300 bg-gray-100 p-0.5">
              <button
                type="button"
                onClick={() => handleScopeChange('overall')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  scope === 'overall'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Overall
              </button>
              <button
                type="button"
                onClick={() => handleScopeChange('match')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  scope === 'match'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                By Match
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Match Picker Dropdown when 'By Match' is active */}
      {scope === 'match' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <span className="text-xs font-bold text-blue-800 uppercase tracking-wider">Filtered Match</span>
            <p className="text-sm text-blue-950 font-medium">Select a match to view game-by-game statistics:</p>
          </div>
          <select
            value={selectedMatchId}
            onChange={(e) => setSelectedMatchId(e.target.value)}
            className="w-full sm:w-72 bg-white border border-blue-300 rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {statsData.matches_list.map(m => (
              <option key={m.match_id} value={m.match_id}>
                {m.name} ({m.score}) - {m.date}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-500 font-medium">Loading statistics...</div>
      ) : (
        <>
          {/* Summary Banner */}
          <div className="bg-slate-900 text-white rounded-2xl p-6 mb-6 shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold">{selectedPlayerName}</h2>
                <span className="bg-slate-800 text-slate-300 text-xs px-3 py-1 rounded-full border border-slate-700">
                  {scope === 'overall' ? `All Matches (${stats.total_matches} Played)` : 'Single Match Breakdown'}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {scope === 'overall' ? 'Career aggregate shot & match stats' : `Match Record & Itemized Breakdown`}
              </p>
            </div>

            <div className="flex items-center gap-6">
              <div className="text-center">
                <span className="block text-2xl font-extrabold text-sky-400">{stats.win_rate}%</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Win Rate</span>
              </div>
              <div className="w-px h-10 bg-slate-800" />
              <div className="text-center">
                <span className="block text-2xl font-extrabold text-emerald-400">{stats.matches_won} - {stats.matches_lost}</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Match W - L</span>
              </div>
              <div className="w-px h-10 bg-slate-800" />
              <div className="text-center">
                <span className="block text-2xl font-extrabold text-amber-400">{stats.games_won} - {stats.games_lost}</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Game W - L</span>
              </div>
            </div>
          </div>

          {/* AI Coaching Analysis Card */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-5 mb-6 shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center text-2xl">
                🤖
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-white">
                    {scope === 'overall' ? `AI Career Coaching Insights` : `AI Match Breakdown`}
                  </h3>
                  <span className="bg-white/20 text-blue-100 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {savedAnalysisInfo ? `✓ Analyzed on ${new Date(savedAnalysisInfo.created_at).toLocaleDateString()}` : 'AI Coach'}
                  </span>
                </div>
                <p className="text-xs text-blue-100 mt-0.5">
                  {savedAnalysisInfo
                    ? `Coaching dossier ready: Overview, main issues, practice drills & coach discussion topics`
                    : scope === 'overall'
                    ? `Get structured overview, main issues, drills & coach discussion points for ${selectedPlayerName}`
                    : `Analyze momentum shifts, serve hold dynamics, and key errors for this match`}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsAIModalOpen(true)}
              className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 text-xs font-bold px-4 py-2.5 rounded-xl shadow-xs transition flex items-center justify-center gap-2"
            >
              <span>{savedAnalysisInfo ? '📊 View AI Coach Report' : '⚡ Get Analysis from AI Coach'}</span>
              <span>→</span>
            </button>
          </div>

          {/* Winners vs Errors Ratio Bar Card */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-bold text-gray-900">Winners vs. Errors Ratio</h3>
              <span className="text-xs font-semibold text-gray-500">
                {stats.total_winners} Winners / {stats.total_errors} Errors
              </span>
            </div>

            <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden flex mb-2">
              <div
                style={{ width: `${Math.max(winnersPct, 5)}%` }}
                className="bg-emerald-500 h-full transition-all duration-500"
              />
              <div
                style={{ width: `${Math.max(errorsPct, 5)}%` }}
                className="bg-rose-500 h-full transition-all duration-500"
              />
            </div>

            <div className="flex justify-between text-xs font-bold">
              <span className="text-emerald-600">⚡ {winnersPct}% Winners ({stats.total_winners})</span>
              <span className="text-rose-600">❌ {errorsPct}% Errors ({stats.total_errors})</span>
            </div>
          </div>

          {/* Itemized Shot Making & Mistakes Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Winners Card */}
            <div className="bg-white rounded-2xl p-6 border border-emerald-200 shadow-sm">
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-emerald-100">
                <h3 className="text-base font-bold text-emerald-800 flex items-center gap-2">
                  ⚡ Winners & Aces
                </h3>
                <span className="text-lg font-extrabold text-emerald-600">{stats.total_winners}</span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">🎯 Aces</span>
                  <span className="font-bold text-emerald-700">{stats.aces}</span>
                </div>
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">🔥 Field / Rally Winners</span>
                  <span className="font-bold text-emerald-700">{stats.winners}</span>
                </div>
              </div>
            </div>

            {/* Errors Card */}
            <div className="bg-white rounded-2xl p-6 border border-rose-200 shadow-sm">
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-rose-100">
                <h3 className="text-base font-bold text-rose-800 flex items-center gap-2">
                  ❌ Errors & Faults
                </h3>
                <span className="text-lg font-extrabold text-rose-600">{stats.total_errors}</span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">⚠️ Double Faults</span>
                  <span className="font-bold text-rose-700">{stats.double_faults}</span>
                </div>
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">💨 Serve Faults</span>
                  <span className="font-bold text-rose-700">{stats.serve_faults}</span>
                </div>
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">💥 Unforced Errors</span>
                  <span className="font-bold text-rose-700">{stats.unforced_errors}</span>
                </div>
                <div className="flex justify-between items-center py-1 text-sm border-b border-gray-100">
                  <span className="text-gray-600">🛡️ Forced Errors</span>
                  <span className="font-bold text-rose-700">{stats.forced_errors}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Serve Performance Card */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm mb-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              🎾 Serve Performance
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-200">
                <span className="block text-xl font-bold text-slate-800">
                  {stats.service_games_won} / {stats.service_games_played}
                </span>
                <span className="text-xs text-gray-500 font-semibold mt-1 block">
                  Service Games Held ({stats.service_hold_rate}%)
                </span>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-200">
                <span className="block text-xl font-bold text-emerald-600">{stats.aces}</span>
                <span className="text-xs text-gray-500 font-semibold mt-1 block">Total Aces</span>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-200">
                <span className="block text-xl font-bold text-rose-600">{stats.double_faults}</span>
                <span className="text-xs text-gray-500 font-semibold mt-1 block">Double Faults</span>
              </div>
            </div>
          </div>

          {/* Matches List */}
          <div className="mt-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {scope === 'overall' ? `Matches involving ${selectedPlayerName}` : 'Selected Match Details'}
            </h3>

            {statsData.matches_breakdown.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {statsData.matches_breakdown.map((m) => (
                  <Link
                    key={m.match_id}
                    to={`/match-details/${m.match_id}`}
                    className="block bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold text-gray-900">{m.name}</h4>
                        <p className="text-xs text-gray-500">{m.date}</p>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                        m.won ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {m.won ? 'WON' : 'LOST'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
                      <span className="text-gray-600">Score: <strong className="text-gray-900">{m.score}</strong></span>
                      <span className="text-xs font-semibold text-blue-600 hover:underline">View Match Details →</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl text-gray-500 text-sm">
                No matches recorded for this player yet.
              </div>
            )}
          </div>
        </>
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        analysisType={scope === 'match' ? 'match' : 'player_overall'}
        matchId={scope === 'match' ? selectedMatchId : null}
        playerId={selectedPlayerId}
        playerName={selectedPlayerName}
        subtitle={scope === 'overall' ? `Career Performance Analysis • ${selectedPlayerName}` : `Match Breakdown • ${selectedPlayerName}`}
        statsSnapshot={stats}
        onAnalysisSaved={(analysis) => setSavedAnalysisInfo(analysis)}
      />
    </div>
  );
};

export default StatsPage;
